import { parseRoleList } from "./auth";

export type UserRole =
  | "admin"
  | "marketing"
  | "clerk"
  | "custodian"
  | "cashier"
  | "accounting"
  | "accounting_ap"
  | "supervisor"
  | "treasury_in_charge"
  | "treasury_assistant"
  | "laboratory_head"
  | "hr_staff"
  | "super_admin";

type RoleBasedAccessConfig = {
  [role in UserRole]: string[];
};

/** All app modules — used by super_admin and as the master list. */
export const allModulePaths = [
  "/dashboard",

  "/daily-sales-report",
  "/credit-billing-statement",
  "/credit-billing-payment",
  "/discount",
  "/borrowed-cash",
  "/advance-payment",
  "/end-of-day",

  "/petty-cash",
  "/petty-cash-entry",
  "/petty-cash-entry/view/:id",
  "/petty-cash-requisition",
  "/petty-cash-requisition/view/:id",
  "/reports",
  "/sales-report",
  "/expense-report",
  "/profit-and-loss-report",
  "/account-receivable-report",
  "/payment-report",

  "/expense",
  "/purchase-request",
  "/purchase-order",
  "/payment-request",

  "/manage",
  "/customer",
  "/expense-type",
  "/branch",
  "/user",
  "/test-price",
];

/**
 * Returns true when any of the user's roles is allowed, or when the user
 * has super_admin (full access to every gated action/button).
 * `roleOrRoles` may be a single codename, comma-separated list, or array.
 */
export const hasRole = (
  roleOrRoles: string | string[] | null | undefined,
  ...allowedRoles: UserRole[]
): boolean => {
  const userRoles = parseRoleList(roleOrRoles);
  if (userRoles.length === 0) return false;
  if (userRoles.includes("super_admin")) return true;
  return userRoles.some((role) => allowedRoles.includes(role as UserRole));
};

/**
 * Like hasRole, but does NOT treat super_admin as every role.
 * Use for exclusion checks: `!hasExactRole(role, "supervisor")`.
 */
export const hasExactRole = (
  roleOrRoles: string | string[] | null | undefined,
  ...allowedRoles: UserRole[]
): boolean => {
  const userRoles = parseRoleList(roleOrRoles);
  if (userRoles.length === 0) return false;
  return userRoles.some((role) => allowedRoles.includes(role as UserRole));
};

export const roleBasedModuleAccess: RoleBasedAccessConfig = {
  marketing: [
    "/dashboard",

    "/expense",
    "/purchase-request",
    "/purchase-order",
  ],
  admin: [
    "/dashboard",

    "/credit-billing-statement",

    "/manage",
    "/user",
    "/branch",
    "/customer",
    "/expense-type",

    "/reports",
    "/sales-report",
    "/expense-report",
    "/profit-and-loss-report",
    "/account-receivable-report",
    "/payment-report",

    "/petty-cash",
    "/petty-cash-entry",
    "/petty-cash-entry/view/:id",
    "/petty-cash-requisition",
    "/discount",
    "/end-of-day",

    "/expense",
    "/purchase-request",
    "/purchase-order",
    "/payment-request",
  ],
  clerk: [
    "/dashboard",

    "/credit-billing-statement",
    "/reports",
    "/account-receivable-report",
  ],
  custodian: [
    "/dashboard",

    "/petty-cash",
    "/petty-cash-entry",
    "/petty-cash-entry/view/:id",
    "/petty-cash-requisition",
    "/petty-cash-requisition/view/:id",
    "/payment-request",
  ],
  cashier: [
    "/dashboard",

    "/manage",
    "/customer",
    "/daily-sales-report",
    "/credit-billing-payment",
    "/end-of-day",
    "/advance-payment",
  ],
  accounting: [
    "/dashboard",

    "/expense",
    "/purchase-request",
    "/purchase-order",
    "/payment-request",

    "/manage",
    "/expense-type",

    "/reports",
    "/sales-report",
    "/expense-report",
    "/profit-and-loss-report",
    "/account-receivable-report",
    "/payment-report",

    "/petty-cash-requisition",
    "/end-of-day",
  ],
  /** Accounts Payable — checks liquidation / payment-request flow. */
  accounting_ap: [
    "/dashboard",

    "/expense",
    "/purchase-request",
    "/purchase-order",
    "/payment-request",

    "/manage",
    "/expense-type",

    "/reports",
    "/expense-report",
    "/payment-report",

    "/petty-cash-requisition",
  ],
  supervisor: [
    "/dashboard",

    "/expense",
    "/purchase-request",
    "/payment-request",
    "/petty-cash",
    "/petty-cash-entry",
    "/petty-cash-requisition",
  ],
  /** Treasury-in-Charge — petty cash requisition / payment-request flow. */
  treasury_in_charge: [
    "/dashboard",

    "/petty-cash-requisition",
    "/payment-request",

    "/reports",
    "/payment-report",
  ],
  /** Assists treasury on requisition / payment request work. */
  treasury_assistant: [
    "/dashboard",

    "/petty-cash-requisition",
    "/payment-request",
  ],
  /** Department head — can raise expense / purchase requests. */
  laboratory_head: [
    "/dashboard",

    "/expense",
    "/purchase-request",
    "/purchase-order",
  ],
  /** HR staff — expense / purchase request for HR needs. */
  hr_staff: [
    "/dashboard",

    "/expense",
    "/purchase-request",
  ],
  super_admin: [...allModulePaths],
};

/** Union of module paths for all of the user's roles. */
export const getAllowedUrlsForRoles = (
  roleOrRoles: string | string[] | null | undefined
): string[] => {
  const roles = parseRoleList(roleOrRoles);
  if (roles.includes("super_admin")) return [...allModulePaths];
  const urls = new Set<string>();
  roles.forEach((role) => {
    const list = roleBasedModuleAccess[role as UserRole];
    if (list) list.forEach((url) => urls.add(url));
  });
  return Array.from(urls);
};

export const hasRouteAccess = (
  roleOrRoles: UserRole | string | string[] | null,
  pathname: string
): boolean => {
  const roles = parseRoleList(roleOrRoles);
  if (roles.length === 0) return false;
  if (roles.includes("super_admin")) return true;
  return roles.some((role) => {
    const allowedUrls = roleBasedModuleAccess[role as UserRole];
    if (!allowedUrls) return false;
    return allowedUrls.some((url) => pathname.startsWith(url));
  });
};
